import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { PlusIcon, TagIcon, PencilIcon } from '@heroicons/react/outline';
import LoadingBar from 'react-top-loading-bar';
import '../../Pagination.css';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import { useGetUserDiscountsQuery } from '../../Features/discountsSlice';
import { useTheme } from "../../ThemeContext";


function ManageDiscountsView({}) {
    const navigate = useNavigate();
    const { darkMode } = useTheme();

    const [progress, setProgress] = useState(0);
    const [numRowsPerPage, setNumRowsPerPage] = useState(10);
    const [currentItems, setCurrentItems] = useState([]);
    const [pageCount, setPageCount] = useState(0);

    const user = useSelector(selectCurrentUser);

    const {
        data: discounts,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetUserDiscountsQuery({
        user_id: user.id
    });

    const [input, setInput] = useState({
        search: ''
    });

    useEffect(() => {
        setProgress(30);

        if (isLoading) {
            setProgress(80);
        }

        if (isSuccess && discounts?.data) {
            setCurrentItems(discounts.data);
            setPageCount(
                Math.ceil(discounts.data.total / numRowsPerPage)
            );
        }

        if (isError) {
            setCurrentItems([]);
        }

        setProgress(100);

        return () => {};
    }, [discounts, isLoading, isSuccess, isError, numRowsPerPage]);

    const editTax = (e, id) => {
        navigate("/client/edit-disocunt/" + id);
    };

    const trancateText = (str) => {
        if (str === null || str === undefined) {
            return '--';
        }

        const value = String(str);

        return value.length > 25
            ? value.substring(0, 25) + "..."
            : value;
    };

    const formatDiscountValue = (data) => {
        if (data?.value === null || data?.value === undefined) {
            return '--';
        }

        if (data.is_percentage) {
            return `${data.value}%`;
        }

        return `${data.value}`;
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            {/* PAGE CONTENT */}
            <div className="w-full px-4 sm:px-6 lg:px-8 py-6">

                {/* HEADER */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">

                    <div className="flex items-center gap-3">

                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center flex-shrink-0">
                            <TagIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
                                Manage Discounts
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Create and manage discounts available to your customers
                            </p>
                        </div>

                    </div>

                    {/* ADD DISCOUNT BUTTON */}
                    {isSuccess && (
                        <Link
                            to="/client/create-discount"
                            className="
                                inline-flex items-center justify-center gap-2
                                w-full sm:w-auto
                                px-4 py-2.5
                                rounded-xl
                                bg-[#80b643]
                                hover:bg-[#719f3a]
                                text-white
                                text-sm font-medium
                                shadow-sm
                                transition-all duration-200
                                hover:shadow-md
                                focus:outline-none
                                focus:ring-2
                                focus:ring-[#80b643]/30
                            "
                        >
                            <PlusIcon className="w-5 h-5" />
                            <span>Add Discount</span>
                        </Link>
                    )}

                </div>


                {/* MAIN CARD */}
                <div className="
                    bg-white dark:bg-gray-900
                    border border-gray-200 dark:border-gray-800
                    rounded-xl
                    shadow-sm
                    overflow-hidden
                ">

                    {/* CARD HEADER */}
                    <div className="
                        flex flex-col sm:flex-row
                        sm:items-center
                        sm:justify-between
                        gap-3
                        px-4 sm:px-5
                        py-4
                        border-b border-gray-200 dark:border-gray-800
                    ">

                        <div>
                            <h2 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white">
                                Discount List
                            </h2>

                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                {currentItems.length > 0
                                    ? `${currentItems.length} discount${currentItems.length === 1 ? '' : 's'} available`
                                    : 'No discounts available'}
                            </p>
                        </div>

                        {/* MOBILE ADD BUTTON */}
                        {isSuccess && (
                            <Link
                                to="/client/create-discount"
                                className="
                                    sm:hidden
                                    inline-flex items-center justify-center gap-2
                                    w-full
                                    px-4 py-2.5
                                    rounded-lg
                                    border border-[#80b643]
                                    text-[#80b643]
                                    hover:bg-[#80b643]/10
                                    text-sm font-medium
                                    transition-colors
                                "
                            >
                                <PlusIcon className="w-4 h-4" />
                                Add Discount
                            </Link>
                        )}

                    </div>


                    {/* TABLE */}
                    {currentItems.length > 0 ? (

                        <div className="overflow-x-auto">

                            <table className="w-full min-w-[650px]">

                                <thead className="
                                    bg-gray-50
                                    dark:bg-gray-800/70
                                    border-b border-gray-200
                                    dark:border-gray-800
                                ">
                                    <tr>

                                        <th
                                            scope="col"
                                            className="
                                                px-5 py-3.5
                                                text-left
                                                text-[11px]
                                                font-semibold
                                                uppercase
                                                tracking-wider
                                                text-gray-500
                                                dark:text-gray-400
                                            "
                                        >
                                            Discount
                                        </th>

                                        <th
                                            scope="col"
                                            className="
                                                px-5 py-3.5
                                                text-left
                                                text-[11px]
                                                font-semibold
                                                uppercase
                                                tracking-wider
                                                text-gray-500
                                                dark:text-gray-400
                                            "
                                        >
                                            Value
                                        </th>

                                        <th
                                            scope="col"
                                            className="
                                                px-5 py-3.5
                                                text-left
                                                text-[11px]
                                                font-semibold
                                                uppercase
                                                tracking-wider
                                                text-gray-500
                                                dark:text-gray-400
                                            "
                                        >
                                            Type
                                        </th>

                                        <th
                                            scope="col"
                                            className="
                                                px-5 py-3.5
                                                text-right
                                                text-[11px]
                                                font-semibold
                                                uppercase
                                                tracking-wider
                                                text-gray-500
                                                dark:text-gray-400
                                            "
                                        >
                                            Action
                                        </th>

                                    </tr>
                                </thead>


                                <tbody className="
                                    divide-y
                                    divide-gray-100
                                    dark:divide-gray-800
                                ">

                                    {currentItems.map((data) => (

                                        <tr
                                            onClick={(e) => editTax(e, data.id)}
                                            key={data.id}
                                            className="
                                                group
                                                cursor-pointer
                                                hover:bg-gray-50
                                                dark:hover:bg-gray-800/50
                                                transition-colors
                                            "
                                        >

                                            {/* DISCOUNT NAME */}
                                            <td className="px-5 py-4">

                                                <div className="flex items-center gap-3">

                                                    <div className="
                                                        w-9 h-9
                                                        rounded-lg
                                                        bg-[#80b643]/10
                                                        dark:bg-[#80b643]/20
                                                        flex items-center
                                                        justify-center
                                                        flex-shrink-0
                                                    ">
                                                        <TagIcon className="w-4.5 h-4.5 text-[#80b643]" />
                                                    </div>

                                                    <div className="min-w-0">

                                                        <div className="
                                                            text-sm
                                                            font-medium
                                                            text-gray-800
                                                            dark:text-white
                                                        ">
                                                            {trancateText(data.name)}
                                                        </div>

                                                        <div className="
                                                            text-xs
                                                            text-gray-400
                                                            dark:text-gray-500
                                                            mt-0.5
                                                        ">
                                                            Discount
                                                        </div>

                                                    </div>

                                                </div>

                                            </td>


                                            {/* VALUE */}
                                            <td className="px-5 py-4">

                                                <span className="
                                                    inline-flex
                                                    items-center
                                                    px-2.5
                                                    py-1
                                                    rounded-lg
                                                    bg-[#80b643]/10
                                                    dark:bg-[#80b643]/20
                                                    text-[#80b643]
                                                    text-sm
                                                    font-semibold
                                                ">
                                                    {formatDiscountValue(data)}
                                                </span>

                                            </td>


                                            {/* TYPE */}
                                            <td className="px-5 py-4">

                                                <span className="
                                                    inline-flex
                                                    items-center
                                                    px-2.5
                                                    py-1
                                                    rounded-lg
                                                    bg-gray-100
                                                    dark:bg-gray-800
                                                    text-gray-600
                                                    dark:text-gray-300
                                                    text-xs
                                                    font-medium
                                                ">
                                                    {data.is_percentage
                                                        ? 'Percentage'
                                                        : 'Fixed Amount'}
                                                </span>

                                            </td>


                                            {/* ACTION */}
                                            <td className="px-5 py-4 text-right">

                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        editTax(e, data.id);
                                                    }}
                                                    className="
                                                        inline-flex
                                                        items-center
                                                        justify-center
                                                        w-8 h-8
                                                        rounded-lg
                                                        text-gray-400
                                                        hover:text-[#80b643]
                                                        hover:bg-[#80b643]/10
                                                        dark:hover:bg-[#80b643]/20
                                                        transition-colors
                                                    "
                                                    title="Edit discount"
                                                >
                                                    <PencilIcon className="w-4 h-4" />
                                                </button>

                                            </td>

                                        </tr>

                                    ))}

                                </tbody>

                            </table>

                        </div>

                    ) : (

                        /* EMPTY STATE */
                        <div className="flex flex-col items-center justify-center px-6 py-16">

                            <div className="
                                w-14 h-14
                                rounded-xl
                                bg-gray-100
                                dark:bg-gray-800
                                flex items-center
                                justify-center
                                mb-4
                            ">
                                <TagIcon className="
                                    w-7 h-7
                                    text-gray-400
                                    dark:text-gray-500
                                " />
                            </div>

                            <h3 className="
                                text-sm
                                font-semibold
                                text-gray-700
                                dark:text-gray-200
                            ">
                                No discounts found
                            </h3>

                            <p className="
                                text-xs sm:text-sm
                                text-gray-500
                                dark:text-gray-400
                                text-center
                                max-w-sm
                                mt-1.5
                            ">
                                You haven't created any discounts yet. Add a discount to make it available for ticket sales.
                            </p>

                            <Link
                                to="/client/create-discount"
                                className="
                                    inline-flex items-center justify-center gap-2
                                    mt-5
                                    px-4 py-2.5
                                    rounded-xl
                                    bg-[#80b643]
                                    hover:bg-[#719f3a]
                                    text-white
                                    text-sm font-medium
                                    shadow-sm
                                    transition-all
                                "
                            >
                                <PlusIcon className="w-5 h-5" />
                                Add Discount
                            </Link>

                        </div>

                    )}

                </div>


                {/* FOOTER / SUMMARY */}
                {currentItems.length > 0 && (
                    <div className="
                        mt-3
                        flex flex-col sm:flex-row
                        sm:items-center
                        sm:justify-between
                        gap-2
                        px-1
                    ">

                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            Showing{' '}
                            <span className="font-medium text-gray-700 dark:text-gray-200">
                                {currentItems.length}
                            </span>{' '}
                            discount{currentItems.length === 1 ? '' : 's'}
                        </div>

                        <div className="text-xs text-gray-400 dark:text-gray-500">
                            Click a discount to edit
                        </div>

                    </div>
                )}

            </div>
        </div>
    );
}

export default ManageDiscountsView;